import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  Facebook,
  Instagram,
  Twitter,
  Youtube,
  ArrowRight,
} from "lucide-react";

const quickLinks = [
  { name: "Home", href: "#home" },
  { name: "Inventory", href: "/car-inventory" },
  { name: "Financing", href: "/finance" },
  { name: "About Us", href: "/about" },
  { name: "Contact", href: "/contact" },
];

// const services = [
//   { name: "Vehicle Sales", href: "#inventory" },
//   { name: "Auto Financing", href: "#financing" },
//   { name: "Trade-Ins", href: "#contact" },
//   { name: "Vehicle Service", href: "#service" },
//   { name: "Extended Warranty", href: "#contact" },
// ];

const toRoute = (hashHref: string) => {
  if (!hashHref.startsWith("#")) return hashHref;
  return hashHref === "#home" ? "/" : `/${hashHref}`;
};

export default function Footer() {
  return (
    <footer id="contact" className="bg-gray-900 text-white w-full">
      {/* Map */}
      <div className="relative h-80 w-full overflow-hidden">
        <iframe
          src="https://www.google.com/maps?q=20308+Logan+Ave,+Langley,+BC+V3A+4L7&z=15&output=embed"
          width="100%"
          height="100%"
          style={{
            border: 0,
            filter: "grayscale(35%) brightness(0.85) contrast(1.05)",
          }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Big Boys Motor Club Location"
          className="block"
        />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-gray-900/85 via-gray-900/20 to-transparent" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 items-start">
          {/* Brand */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="mb-6">
              <span className="font-serif text-2xl font-bold">
                <span className="text-brand-red">BIG BOYS</span>
                <span className="text-white"> MOTOR</span>
                <span className="text-brand-gold"> CLUB</span>
              </span>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed mb-6">
              Where luxury meets value. Your trusted partner for premium
              pre-owned vehicles and exceptional automotive services.
            </p>
            <div className="flex gap-3">
              <a
                href="#"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-brand-red transition-colors"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="#"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-brand-red transition-colors"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="#"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-brand-red transition-colors"
              >
                <Twitter className="w-5 h-5" />
              </a>
              <a
                href="#"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-brand-red transition-colors"
              >
                <Youtube className="w-5 h-5" />
              </a>
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:justify-self-center"
          >
            <h4 className="font-serif text-lg font-bold mb-6 lg:text-center">
              Quick Links
            </h4>
            <ul className="space-y-3 lg:text-center">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={toRoute(link.href)}
                    className="text-gray-400 hover:text-brand-red transition-colors inline-flex items-center gap-2 group"
                  >
                    <ArrowRight className="w-4 h-4 opacity-0 -ml-6 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Services */}
          {/* <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h4 className="font-serif text-lg font-bold mb-6">Our Services</h4>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service.name}>
                  <Link
                    to={toRoute(service.href)}
                    className="text-gray-400 hover:text-brand-red transition-colors flex items-center gap-2 group"
                  >
                    <ArrowRight className="w-4 h-4 opacity-0 -ml-6 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div> */}

          {/* Contact */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="lg:justify-self-end"
          >
            <h4 className="font-serif text-lg font-bold mb-6">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-brand-red flex-shrink-0 mt-0.5" />
                <span className="text-gray-400 text-sm">
                  20308 Logan Ave
                  <br />
                  Langley, BC V3A 4L7
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-brand-red flex-shrink-0" />
                <div className="text-sm">
                  <p className="text-gray-400">Sales: (604) 358-8081</p>
                  <p className="text-gray-400">Service: (604) 358-8081</p>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-brand-red flex-shrink-0" />
                <a
                  href="mailto:info@bigboysmotorclub.com"
                  className="text-gray-400 hover:text-brand-red transition-colors text-sm"
                >
                  info@bigboysmotorclub.com
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-brand-red flex-shrink-0" />
                <span className="text-gray-400 text-sm">
                  Mon - Sat: 9AM - 8PM
                  <br />
                  Sunday: 11AM - 6PM
                </span>
              </li>
            </ul>

            <motion.a
              href="https://maps.google.com"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-brand-red text-white rounded-full font-semibold hover:bg-red-700 transition-colors"
            >
              Get Directions
              <ArrowRight className="w-4 h-4" />
            </motion.a>
          </motion.div>
        </div>

        {/* Bottom */}
        <div className="mt-16 pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-500 text-sm text-center md:text-left">
              © {new Date().getFullYear()} Big Boys Motor Club. All rights
              reserved.
              <span className="hidden md:inline"> | </span>
              <br className="md:hidden" />
              <Link
                to="/privacy-policy"
                className="hover:text-brand-red transition-colors"
              >
                Privacy Policy
              </Link>
              <span className="mx-2">|</span>
              <Link
                to="/terms-and-conditions"
                className="hover:text-brand-red transition-colors"
              >
                Terms & Conditions
              </Link>
            </p>
            <p className="text-gray-600 text-xs">
              Designed with passion by Square Trigger
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
